import ENV from './Environment';
import Requests from './Requests';
import AuthUtils from './AuthUtils';

const baseUrl = `${ENV.base_url}/api/v1/search_wizard`;
const listFilesUrl = `${baseUrl}/list-files/knowledge-center`;
const teamWorkBaseUrl = `${ENV.base_url}/api/v1/teamwork`;

const normalizePath = (path) => {
	if (!path) return '';
	return path.replace(/\/+/g, '/').trim();
};

const isValidFolderPath = (path) => {
	if (typeof path !== 'string') {
		return false;
	}

	const prohibitedChars = /[<>:"|?*]/;
	if (prohibitedChars.test(path)) {
		return false;
	}

	if (path.length > 1024) {
		return false;
	}

	return true;
};

// Remove the duplicate JWT validation function - we'll use AuthUtils instead

const listFolderContents = async (folderPath, jwtToken, userData) => {
	if (!AuthUtils.isValidJwtToken(jwtToken)) {
		// Use the utility function
		throw new Error('Invalid authentication token');
	}

	if (!isValidFolderPath(folderPath)) {
		throw new Error('Invalid folder path');
	}

	try {
		const normalizedFolderPath = normalizePath(folderPath);

		const url = normalizedFolderPath
			? `${listFilesUrl}?folder=${encodeURIComponent(normalizedFolderPath)}`
			: listFilesUrl;

		const result = await Requests.getRequest(url, jwtToken, userData);

		if (!result) {
			throw new Error('No response received from server');
		}

		if (!result.ok) {
			const errorData = await result.json();
			throw new Error(errorData.message || 'Unable to fetch folder contents');
		}

		const data = await result.json();
		if (!data || !data.s3FileAndDirectoryListList) {
			throw new Error('Invalid response format from server');
		}

		return data;
	} catch (error) {
		throw error;
	}
};

const getFolderStructure = async (jwtToken, userData) => {
	if (!AuthUtils.isValidJwtToken(jwtToken)) {
		// Use the utility function
		throw new Error('Authentication token is required');
	}

	try {
		const rootContents = await listFolderContents('', jwtToken, userData);
		if (!rootContents || !rootContents.s3FileAndDirectoryListList) {
			throw new Error('Invalid folder structure response');
		}

		return rootContents.s3FileAndDirectoryListList.map((item) => ({
			name: item.filename.replace(/^\/|\/$/g, ''),
			isDirectory: item.directory,
		}));
	} catch (error) {
		throw error;
	}
};

const navigateToFolder = async (folderPath, jwtToken, userData) => {
	if (!AuthUtils.isValidJwtToken(jwtToken)) {
		// Use the utility function
		throw new Error('Authentication token is required');
	}

	try {
		const normalizedFolderPath = normalizePath(folderPath);

		const contents = await listFolderContents(
			normalizedFolderPath,
			jwtToken,
			userData
		);

		if (!contents || !contents.s3FileAndDirectoryListList) {
			throw new Error('Invalid folder contents response');
		}

		const processedContents = contents.s3FileAndDirectoryListList
			.filter((item) => item.filename && item.filename.trim())
			.map((item) => {
				const pathParts = item.filename.split('/').filter((part) => part);
				const name =
					pathParts.length > 0 ? pathParts[pathParts.length - 1] : '';

				return {
					name: name,
					isDirectory: item.directory,
					fullPath: item.filename,
					fileType:
						!item.directory && name.includes('.')
							? name.split('.').pop().toLowerCase()
							: null,
					originalItem: { ...item },
				};
			});

		return {
			currentFolder: normalizedFolderPath,
			contents: processedContents,
			_rawBackendResponse: contents,
		};
	} catch (error) {
		throw error;
	}
};

const searchInFolder = async (folderName, searchTerm, jwtToken, userData) => {
	if (!AuthUtils.isValidJwtToken(jwtToken)) {
		// Use the utility function
		throw new Error('Authentication token is required');
	}

	try {
		const normalizedFolderName = normalizePath(folderName);

		const url = `${listFilesUrl}?folder=${encodeURIComponent(
			normalizedFolderName
		)}&searchText=${encodeURIComponent(searchTerm)}`;

		const result = await Requests.getRequest(url, jwtToken, userData);

		if (!result) {
			throw new Error('No response received from server');
		}

		if (!result.ok) {
			throw new Error('Unable to search in folder');
		}

		const data = await result.json();
		if (!data || !data.s3FileAndDirectoryListList) {
			throw new Error('Invalid search response format');
		}

		return {
			currentFolder: data.currentFolder,
			results: data.s3FileAndDirectoryListList.map((item) => {
				const relativePath = item.filename.startsWith(folderName + '/')
					? item.filename.slice((folderName + '/').length)
					: item.filename;

				return {
					name: relativePath.replace(/^\/|\/$/g, ''),
					isDirectory: item.directory,
					fullPath: item.filename,
					fileType: item.filename.split('.').pop().toLowerCase(),
				};
			}),
		};
	} catch (error) {
		throw error;
	}
};

const searchTasksByTag = async (tagName, jwtToken, userData) => {
	if (!AuthUtils.isValidJwtToken(jwtToken)) {
		throw new Error('Authentication token is required');
	}

	if (!tagName || typeof tagName !== 'string' || tagName.trim() === '') {
		throw new Error('Tag name must be a non-empty string');
	}

	try {
		const normalizedTag = tagName.trim();
		const url = `${teamWorkBaseUrl}/tasks-by-tag?tagKey=${encodeURIComponent(
			normalizedTag
		)}`;

		const result = await Requests.getRequest(url, jwtToken, userData);

		if (!result) {
			throw new Error('No response received from server');
		}

		if (!result.ok) {
			// Try to get the error response body
			try {
				// Get raw text response
				const rawText = await result.text();

				// Try to parse as JSON if possible
				try {
					const errorData = JSON.parse(rawText);
					throw new Error(
						`Failed to fetch tasks by tag: HTTP ${result.status} - ${
							errorData.message || errorData.error || JSON.stringify(errorData)
						}`
					);
				} catch (jsonError) {
					throw new Error(
						`Failed to fetch tasks by tag: HTTP ${result.status} - ${
							rawText || 'No error details available'
						}`
					);
				}
			} catch (responseError) {
				throw new Error(`Failed to fetch tasks by tag: HTTP ${result.status}`);
			}
		}

		const data = await result.json();

		if (!Array.isArray(data)) {
			throw new Error('Invalid response format: Expected an array of tasks');
		}

		return data;
	} catch (error) {
		throw error;
	}
};

const getBreadcrumbPath = (folderPath) => {
	if (!folderPath) return [];

	const parts = folderPath.split('/').filter((part) => part);
	return parts.map((part, index) => ({
		name: part,
		path: parts.slice(0, index + 1).join('/'),
		isLast: index === parts.length - 1,
	}));
};

const SearchWizardService = {
	listFolderContents,
	getFolderStructure,
	navigateToFolder,
	searchInFolder,
	searchTasksByTag,
	getBreadcrumbPath,
};

export default SearchWizardService;
